import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import { MenuItem, Box } from "@mui/material";
import XOutlinedTextField from "../../components-v2/base-ui/XOutlinedTextField";

const MsspSelector = ({
  currentRealmId,
  rawAgencies,
  rawMsspMasterRealm,
  onSwitchTo,
}: {
  currentRealmId: string;
  rawAgencies: string | undefined | null;
  rawMsspMasterRealm: string | undefined | null;
  onSwitchTo: (realmId: string, masterRealm?: string) => void;
}) => {
  const [agency, setAgency] = useState<string | undefined>(currentRealmId);

  const agencies: { label: string; value: string }[] = useMemo(() => {
    let tempAgencies: { label: string; value: string }[] = [];
    if (rawMsspMasterRealm) {
      try {
        const msspMaster = JSON.parse(rawMsspMasterRealm);
        // Always include the master realm in the list
        tempAgencies.push({
          label: msspMaster["mssp_master_name"],
          value: msspMaster["mssp_master_realm"],
        });
      } catch (e) {
        console.error("Error in converting rawMsspMasterRealm:", e);
      }
    }
    if (rawAgencies) {
      try {
        tempAgencies.push(
          ...Object.entries<string>(JSON.parse(rawAgencies)).map(
            ([key, value]) => ({
              label: value,
              value: key,
            })
          )
        );
      } catch (e) {
        console.error("Error in converting agencies:", e);
      }
    }
    return tempAgencies;
  }, [rawAgencies, rawMsspMasterRealm]);

  useEffect(() => {
    // Set the initial selected agency based on currentRealmId
    if (currentRealmId) {
      const match = agencies.find(
        (a) => a.value === currentRealmId || a.label === currentRealmId
      );
      if (match) {
        setAgency(match.value);
      } else {
        setAgency(currentRealmId);
      }
    }
  }, [currentRealmId, agencies]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedRealmId = e.target.value;
      setAgency(selectedRealmId);
      
      if (rawMsspMasterRealm) {
        try {
          const msspMaster = JSON.parse(rawMsspMasterRealm);
          // If switching to a tenant (not the master), pass the master realm ID
          if (selectedRealmId !== msspMaster["mssp_master_realm"]) {
            onSwitchTo(selectedRealmId, msspMaster["mssp_master_realm"]);
            return;
          }
        } catch (e) {
          console.error("Error in converting rawMsspMasterRealm:", e);
        }
      }

      onSwitchTo(selectedRealmId);
    },
    [onSwitchTo, rawMsspMasterRealm]
  );

  const currentAgencyLabel = useMemo(() => {
    const selectedAgency = agencies.find((a) => a.value === agency);
    return selectedAgency ? selectedAgency.label : "";
  }, [agency, agencies]);

  return agencies.length > 0 ? (
    <Box px={2}>
      <XOutlinedTextField
        select
        label={`Tenants${currentAgencyLabel ? ` - ${currentAgencyLabel}` : ""}`}
        variant="outlined"
        size="small"
        fullWidth
        value={agency}
        onChange={onChange}
      >
        {agencies.map((agc) => (
          <MenuItem key={agc.value} value={agc.value}>
            {agc.label}
          </MenuItem>
        ))}
      </XOutlinedTextField>
    </Box>
  ) : (
    <></>
  );
};

export default memo(MsspSelector);